import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { AlertTriangle, Shield, Phone, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { db } from '../db';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScamAlertsModal = ({ open, onClose, destination }) => {
  const { t } = useTranslation();
  const [scamData, setScamData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedScam, setExpandedScam] = useState(null);

  useEffect(() => {
    if (open && destination) {
      loadScamData();
    }
  }, [open, destination]);

  const loadScamData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Check local cache first
      const cached = await db.settings.get(`scam_alerts_${destination.toLowerCase()}`);
      if (cached?.value) {
        const parsed = JSON.parse(cached.value);
        const expiresAt = new Date(parsed.expires_at);
        if (new Date() < expiresAt) {
          setScamData(parsed);
          setIsLoading(false);
          return;
        }
      }
      
      // Fetch from API
      const response = await axios.get(`${API}/safety/scams/${encodeURIComponent(destination)}`);
      const data = response.data.data;
      
      // Cache locally
      await db.settings.put({
        key: `scam_alerts_${destination.toLowerCase()}`,
        value: JSON.stringify(data)
      });
      
      setScamData(data);
    } catch (err) {
      console.error('Failed to load scam alerts:', err);
      setError('Failed to load safety information');
    } finally {
      setIsLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'high': return t('high_risk');
      case 'medium': return t('medium_risk');
      case 'low': return t('low_risk');
      default: return severity;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'transport': return '🚕';
      case 'shopping': return '🛍️';
      case 'food': return '🍽️';
      case 'accommodation': return '🏨';
      case 'street': return '🚶';
      case 'romance': return '💔';
      case 'online': return '💻';
      default: return '⚠️';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            {t('stay_safe_in', { destination: destination || 'Your Destination' })}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh] pr-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Loading safety information...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertTriangle className="w-8 h-8 text-destructive mb-4" />
              <p className="text-muted-foreground">{error}</p>
              <Button variant="outline" onClick={loadScamData} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : scamData ? (
            <div className="space-y-4">
              {/* Scams List */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  {t('common_scams')}
                </h3>
                
                {scamData.scams?.map((scam, index) => (
                  <Card 
                    key={index} 
                    className="bg-background/50 border-border cursor-pointer hover:bg-background/80 transition-colors"
                    onClick={() => setExpandedScam(expandedScam === index ? null : index)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <span className="text-2xl">{getCategoryIcon(scam.category)}</span>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold">{scam.name}</h4>
                              <Badge className={getSeverityColor(scam.severity)}>
                                {getSeverityLabel(scam.severity)}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {scam.description}
                            </p>
                          </div>
                        </div>
                        {expandedScam === index ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        )}
                      </div>
                      
                      {expandedScam === index && (
                        <div className="mt-4 pt-4 border-t border-border space-y-3 animate-in">
                          <div>
                            <h5 className="text-sm font-medium text-primary mb-1">
                              {t('how_to_avoid')}
                            </h5>
                            <p className="text-sm text-muted-foreground">
                              {scam.how_to_avoid}
                            </p>
                          </div>
                          
                          {scam.red_flags && scam.red_flags.length > 0 && (
                            <div>
                              <h5 className="text-sm font-medium text-destructive mb-1">
                                {t('red_flags')}
                              </h5>
                              <ul className="text-sm text-muted-foreground space-y-1">
                                {scam.red_flags.map((flag, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-destructive">•</span>
                                    {flag}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* General Tips */}
              {scamData.general_tips && scamData.general_tips.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">
                    General Safety Tips
                  </h3>
                  <Card className="bg-primary/5 border-primary/20">
                    <CardContent className="p-4">
                      <ul className="space-y-2">
                        {scamData.general_tips.map((tip, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm">
                            <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Emergency Contacts */}
              {scamData.emergency && (
                <div className="mt-6">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">
                    {t('emergency_contacts')}
                  </h3>
                  <Card className="bg-destructive/5 border-destructive/20">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        {scamData.emergency.police && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              Police
                            </span>
                            <span className="font-mono font-semibold">{scamData.emergency.police}</span>
                          </div>
                        )}
                        {scamData.emergency.tourist_police && scamData.emergency.tourist_police !== 'N/A' && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              Tourist Police
                            </span>
                            <span className="font-mono font-semibold">{scamData.emergency.tourist_police}</span>
                          </div>
                        )}
                        {scamData.emergency.ambulance && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              Ambulance
                            </span>
                            <span className="font-mono font-semibold">{scamData.emergency.ambulance}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Last Updated */}
              {scamData.generated_at && (
                <p className="text-xs text-center text-muted-foreground mt-4">
                  {t('last_updated')}: {new Date(scamData.generated_at).toLocaleDateString()}
                </p>
              )}
            </div>
          ) : null}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default ScamAlertsModal;
